import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import Aura from '@primevue/themes/aura'
import { definePreset, palette } from '@primevue/themes'

// Brand palette derived from Bulma's $primary so PrimeVue + Bulma + the
// navbar all use the same blue (#1679ca).
const StmncPreset = definePreset(Aura, {
  semantic: {
    primary: palette('#1679ca'),
  },
})
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import 'primeicons/primeicons.css'

import App from './App.vue'
import router from './router'
import { authReady, auth } from './firebase'
import { useUserStore } from './stores/user'
import { useMeetingStore } from './stores/meeting'
// Importing useTheme here wires up the watcher that persists theme changes
// to localStorage and applies the data-theme attribute on <html>.
import './composables/useTheme'

async function bootstrap() {
  const app = createApp(App)
  const pinia = createPinia()

  app.use(pinia)
  app.use(PrimeVue, {
    theme: {
      preset: StmncPreset,
      options: {
        darkModeSelector: '.app-dark',
      },
    },
    ripple: true,
  })
  app.use(ToastService)
  app.use(ConfirmationService)

  // Resolve initial auth state before mounting so the router guard has a
  // deterministic answer on first navigation.
  const user = await authReady
  const userStore = useUserStore()
  const meetingStore = useMeetingStore()

  if (user) {
    await userStore.fetchUserProfile(user)
    // Only subscribe once we have an authenticated user — the `meetings`
    // collection is gated by request.auth in Firestore rules, so an
    // anonymous listener just logs a permission-denied error.
    meetingStore.subscribe()
  }

  // Keep the user profile + votes in sync when auth changes after load
  // (e.g. after login or signout). Also re-subscribe to the current meeting
  // so the Firestore listener runs with the correct auth token.
  let lastUid = user?.uid ?? null
  auth.onAuthStateChanged(async (u) => {
    if (u) {
      await userStore.fetchUserProfile(u)
      if (u.uid !== lastUid) {
        lastUid = u.uid
        meetingStore.subscribe()
      }
    } else {
      lastUid = null
      userStore.profile = {}
      userStore.votes = []
      meetingStore.unsubscribe()
    }
  })

  app.use(router)
  await router.isReady()
  app.mount('#app')
}

bootstrap()
